import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Title,
  Stack,
  useMantineColorScheme,
  Box,
  Card,
  Group,
  RingProgress,
  Avatar,
  Loader,
  Badge,
  SimpleGrid,
} from '@mantine/core';
import { IconCrown, IconTrophy } from '@tabler/icons-react';
import { RankingEntrada, RankingDTO } from '../types/dashboard';
import { useQuery } from '@tanstack/react-query';
import { rankingQuery } from '../queries/appQueries';
import { profileService } from '../services/profileService';
import PageHeader from '../components/ui/PageHeader';

// --- Helpers de presentación -------------------------------------------------

// Entrada del ranking normalizada para la UI (sólo datos reales del backend).
const mapEntrada = (e: RankingEntrada) => ({
  position: e.posicion,
  usuarioId: e.usuarioId,
  name: e.nombre,
  avatarUrl: profileService.getAvatarUrl(e.avatar ?? null),
  score: e.promedio,
  examenes: e.examenes,
  isCurrentUser: e.esActual,
});
type Entry = ReturnType<typeof mapEntrada>;

const initials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

// Color uniforme para todos los avatares de iniciales.
const AVATAR_COLOR = 'gray';

// Oro / plata / bronce para el top 3.
const medalColor = (position: number) =>
  position === 1 ? '#eab308' : position === 2 ? '#94a3b8' : position === 3 ? '#cd7f32' : null;

// --- Laurel (corona) ---------------------------------------------------------

const LAUREL_LEAVES = [
  { cx: 12, cy: 43, r: -15 },
  { cx: 9, cy: 35, r: -32 },
  { cx: 7, cy: 27, r: -50 },
  { cx: 7, cy: 19, r: -68 },
  { cx: 9, cy: 12, r: -84 },
];

const LaurelBranch: React.FC<{ color: string }> = ({ color }) => (
  <g>
    <path d="M14 49 Q5 31 11 9" fill="none" stroke={color} strokeWidth={1.4} strokeLinecap="round" opacity={0.85} />
    {LAUREL_LEAVES.map((l, i) => (
      <ellipse key={i} cx={l.cx} cy={l.cy} rx={4} ry={2} fill={color} transform={`rotate(${l.r} ${l.cx} ${l.cy})`} />
    ))}
  </g>
);

// Número rodeado por dos ramas de laurel (top 3) o número simple (resto).
const PositionMark: React.FC<{ position: number; size?: number }> = ({ position, size = 52 }) => {
  const { colorScheme } = useMantineColorScheme();
  const color = medalColor(position);
  if (!color) {
    return (
      <Box style={{ width: size, textAlign: 'center', flexShrink: 0 }}>
        <Text
          style={{
            fontFamily: 'Space Grotesk',
            fontWeight: 800,
            fontSize: size * 0.4,
            color: colorScheme === 'dark' ? '#475569' : '#cbd5e1',
          }}
        >
          {position}
        </Text>
      </Box>
    );
  }
  return (
    <Box style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 56 56" style={{ position: 'absolute', inset: 0 }}>
        <LaurelBranch color={color} />
        <g transform="translate(56,0) scale(-1,1)">
          <LaurelBranch color={color} />
        </g>
      </svg>
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={{ fontFamily: 'Space Grotesk', fontWeight: 800, fontSize: size * 0.4, color }}>{position}</Text>
      </Box>
    </Box>
  );
};

// --- Página ------------------------------------------------------------------

const RankingsPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const dark = colorScheme === 'dark';
  const navigate = useNavigate();
  const verPerfil = (usuarioId?: number) => {
    if (usuarioId) navigate(`/perfil/${usuarioId}`);
  };

  const rankingQ = useQuery(rankingQuery(50));
  const rankingResp = rankingQ.data as RankingDTO | undefined;
  const rankingData: Entry[] = (rankingResp?.top ?? []).map(mapEntrada);
  const currentUser: Entry | null = rankingResp?.usuarioActual ? mapEntrada(rankingResp.usuarioActual) : null;

  const cardBg = dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(255, 255, 255, 0.9)';
  const cardBorder = `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`;

  const podium = rankingData.slice(0, 3);
  // En pantalla el orden es 2º · 1º · 3º (el del centro más alto).
  const podiumOrder = [podium[1], podium[0], podium[2]];
  const userInTop = currentUser ? rankingData.some((p) => p.usuarioId === currentUser.usuarioId) : false;
  // Estudiantes (de la lista cargada) que están por debajo de tu posición.
  const estudiantesDebajo = currentUser
    ? rankingData.filter((p) => p.position > currentUser.position).length
    : 0;

  // Celda de KPI para el resumen de tu posición.
  const Kpi: React.FC<{ value: React.ReactNode; label: string; color: string }> = ({ value, label, color }) => (
    <Box
      style={{
        padding: '12px 14px',
        borderRadius: 12,
        backgroundColor: 'transparent',
        border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.4)' : '#1e293b'}`,
      }}
    >
      <Text style={{ fontFamily: 'Space Grotesk', fontWeight: 800, fontSize: 22, color, lineHeight: 1.1 }}>
        {value}
      </Text>
      <Text size="xs" c="dimmed" mt={2}>
        {label}
      </Text>
    </Box>
  );

  // --- Fila de la lista ---
  const Row: React.FC<{ player: Entry }> = ({ player }) => (
    <Box
      onClick={() => verPerfil(player.usuarioId)}
      style={{
        backgroundColor: player.isCurrentUser
          ? dark
            ? 'rgba(16, 185, 129, 0.1)'
            : 'rgba(16, 185, 129, 0.06)'
          : dark
          ? 'rgba(51, 65, 85, 0.35)'
          : '#ffffff',
        border: player.isCurrentUser
          ? '1.5px solid #10b981'
          : `1px solid ${dark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
        borderRadius: 14,
        padding: '12px 16px',
        cursor: 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = dark ? '0 6px 18px rgba(0,0,0,0.3)' : '0 6px 18px rgba(0,0,0,0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'none';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="md" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
          <PositionMark position={player.position} />
          <Avatar src={player.avatarUrl} color={AVATAR_COLOR} radius="xl" size={44}>
            {initials(player.name)}
          </Avatar>
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} align="center" wrap="nowrap">
              <Text
                fw={player.isCurrentUser ? 800 : 700}
                lineClamp={1}
                style={{
                  fontFamily: 'Space Grotesk',
                  color: player.isCurrentUser ? '#10b981' : dark ? '#e2e8f0' : '#1e293b',
                }}
              >
                {player.name}
              </Text>
              {player.isCurrentUser && (
                <Badge size="xs" color="teal" variant="light">
                  Tú
                </Badge>
              )}
            </Group>
            <Text size="xs" c="dimmed">
              {player.examenes} examen{player.examenes === 1 ? '' : 'es'} · Posición #{player.position}
            </Text>
          </Box>
        </Group>

        <RingProgress
          size={56}
          thickness={5}
          roundCaps
          sections={[{ value: Math.min(100, player.score), color: '#10b981' }]}
          label={
            <Text ta="center" fw={700} style={{ fontFamily: 'Space Grotesk', fontSize: 13 }}>
              {Math.round(player.score)}%
            </Text>
          }
        />
      </Group>
    </Box>
  );

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Box style={{ padding: '16px 24px', overflowY: 'auto', flex: 1, width: '100%' }}>
        {/* Cabecera de página (fuera del card, como el resto de pantallas) */}
        <Box mb="md">
          <PageHeader
            title="Rankings y Competencia"
            subtitle="Compite con otros estudiantes y alcanza la cima del leaderboard ENARM"
          />
        </Box>

        {/* Hero + Podio */}
        <Card
          padding="xl"
          radius="lg"
          mb="lg"
          style={{ backgroundColor: cardBg, border: cardBorder, overflow: 'hidden' }}
        >
          <Group justify="space-between" align="center" wrap="wrap" gap="xl">
            <Box style={{ flex: 1, minWidth: 260 }}>
              <Group gap={8} mb="sm" align="center">
                <IconTrophy size={18} style={{ color: '#10b981' }} />
                <Text fw={700} style={{ fontFamily: 'Space Grotesk', color: dark ? '#e2e8f0' : '#1e293b' }}>
                  Tu desempeño
                </Text>
              </Group>
              <SimpleGrid cols={2} spacing="sm" style={{ maxWidth: 360 }}>
                <Kpi value={currentUser ? `#${currentUser.position}` : '—'} label="Posición" color="#14b8a6" />
                <Kpi value={currentUser ? `${Math.round(currentUser.score)}%` : '—'} label="Promedio" color="#10b981" />
                <Kpi value={currentUser ? currentUser.examenes : '—'} label="Exámenes" color="#3b82f6" />
                <Kpi value={currentUser ? estudiantesDebajo : '—'} label="Por debajo de ti" color="#8b5cf6" />
              </SimpleGrid>
            </Box>

            {/* Podio con escalones */}
            {podium.length > 0 && (
              <Group align="flex-end" justify="center" gap="sm" style={{ flex: '0 0 auto' }}>
                {podiumOrder.map((player, i) => {
                  if (!player) return null;
                  const isFirst = player.position === 1;
                  const height = player.position === 1 ? 118 : player.position === 2 ? 88 : 70;
                  const color = medalColor(player.position) || '#94a3b8';
                  return (
                    <Stack
                      key={player.usuarioId}
                      align="center"
                      gap={6}
                      onClick={() => verPerfil(player.usuarioId)}
                      style={{ cursor: 'pointer', width: 92 }}
                    >
                      {isFirst && <IconCrown size={22} style={{ color }} />}
                      <Avatar
                        src={player.avatarUrl}
                        color={AVATAR_COLOR}
                        radius="xl"
                        size={isFirst ? 58 : 48}
                        style={{ border: `3px solid ${color}` }}
                      >
                        {initials(player.name)}
                      </Avatar>
                      <Text fw={700} size="xs" ta="center" lineClamp={1} style={{ fontFamily: 'Space Grotesk', maxWidth: 88 }}>
                        {player.name}
                      </Text>
                      <Text size="xs" fw={700} style={{ color: '#10b981' }}>
                        {Math.round(player.score)}%
                      </Text>
                      <Box
                        style={{
                          width: '100%',
                          height,
                          backgroundColor: color,
                          borderRadius: '10px 10px 0 0',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          paddingBottom: 8,
                          boxShadow: 'inset 0 -4px 12px rgba(0,0,0,0.12)',
                        }}
                      >
                        <Text style={{ fontFamily: 'Space Grotesk', fontWeight: 800, fontSize: 30, color: 'rgba(255,255,255,0.95)' }}>
                          {player.position}
                        </Text>
                      </Box>
                    </Stack>
                  );
                })}
              </Group>
            )}
          </Group>
        </Card>

        {/* Lista completa */}
        <Card padding="lg" radius="lg" style={{ backgroundColor: cardBg, border: cardBorder }}>
          <Group justify="space-between" align="center" mb="md">
            <Title order={4} style={{ fontFamily: 'Space Grotesk' }}>
              Ranking completo
            </Title>
            <Text size="xs" c="dimmed">
              {rankingData.length} estudiantes
            </Text>
          </Group>

          {rankingQ.isLoading ? (
            <Group justify="center" py="xl">
              <Loader size="sm" color="teal" />
            </Group>
          ) : rankingData.length === 0 ? (
            <Text c="dimmed" ta="center" py="xl" size="sm">
              Aún no hay datos de ranking disponibles.
            </Text>
          ) : (
            <Stack gap="sm">
              {rankingData.map((player) => (
                <Row key={player.usuarioId} player={player} />
              ))}

              {/* Tu fila fijada si no estás en el top mostrado */}
              {currentUser && !userInTop && (
                <>
                  <Text ta="center" c="dimmed" size="xs">
                    • • •
                  </Text>
                  <Row player={currentUser} />
                </>
              )}
            </Stack>
          )}
        </Card>
      </Box>

      {/* Footer */}
      <Box
        style={{
          borderTop: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.5)'}`,
          textAlign: 'center',
          padding: '1rem',
          backgroundColor: dark ? 'rgba(15, 23, 42, 0.8)' : 'rgba(247, 243, 238, 0.8)',
          marginTop: 'auto',
        }}
      >
        <Text size="xs" style={{ color: dark ? '#94a3b8' : '#5a5550', fontFamily: 'Inter, sans-serif' }}>
          © 2024 ENARM360. Todos los derechos reservados.
        </Text>
      </Box>
    </Box>
  );
};

export default RankingsPage;
